import { AnalyticsEvents, trackEvent } from "@axqora/analytics";
import { useAppStoreHref } from "@axqora/ui";
import { ArrowDown } from "lucide-react";
import { motion } from "framer-motion";
import { screenshots } from "../assets/screenshots";
import { AppStoreButton } from "../components/AppStoreButton";
import { DeviceMockup } from "../components/DeviceMockup";
import { prismarkConfig } from "../config";
import type { LandingContent, Locale } from "../content/types";

type HeroSectionProps = {
  content: LandingContent["hero"];
  locale: Locale;
};

export function HeroSection({ content, locale }: HeroSectionProps) {
  const appStoreHref = useAppStoreHref(prismarkConfig.appStoreUrl[locale], locale);
  return (
    <section className="hero-section" id="top">
      <div className="hero-ambient" aria-hidden="true" />
      <div className="site-container hero-grid">
        <motion.div className="hero-copy" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
          <img className="hero-app-icon" src={screenshots.icon} alt="Prismark app icon" width={72} height={72} />
          <h1>{content.title}</h1>
          <p className="hero-headline">{content.headline}</p>
          <p className="hero-body">{content.body}</p>
          <div className="hero-actions">
            <AppStoreButton
              href={appStoreHref}
              onClick={() =>
                trackEvent(AnalyticsEvents.CtaClick, {
                  product: "prismark",
                  page: "home",
                  locale,
                  target: "hero_app_store"
                })
              }
            >
              {content.primaryCta}
            </AppStoreButton>
            <a
              className="secondary-link"
              href="#features"
              onClick={(event) => {
                const features = document.getElementById("features");
                const target = features && features.offsetParent !== null ? features : document.getElementById("capabilities");
                if (target) {
                  event.preventDefault();
                  target.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              {content.secondaryCta}
              <ArrowDown size={17} aria-hidden="true" />
            </a>
          </div>
        </motion.div>

        <motion.div
          className="hero-visual"
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.1 }}
        >
          <DeviceMockup src={screenshots.camera} alt="Prismark photo capture interface" priority size="hero" />
          <span className="float-label float-label-1">Photo</span>
          <span className="float-label float-label-2">Dual Camera</span>
          <span className="float-label float-label-3">Histogram</span>
          <span className="float-label float-label-4">Gallery</span>
        </motion.div>
      </div>
    </section>
  );
}
